import TextField from '@mui/material/TextField';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import useMediaQuery from '@mui/material/useMediaQuery';
import ListSubheader from '@mui/material/ListSubheader';
import Popper from '@mui/material/Popper';
import { useTheme, styled } from '@mui/material/styles';
import { VariableSizeList } from 'react-window';
import Typography from '@mui/material/Typography';
import React, { createContext, forwardRef, useContext, useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form';

const LISTBOX_PADDING = 8; // px
let labelsArr =  [];

function renderRow(props) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: style.top + LISTBOX_PADDING,
  };

  const getOptionLabel = (option) => {
    const labelsArray = labelsArr.map(lab => `${option[lab]}`);
    const label = labelsArray.join(" - ");
    return label;
  }

  if (dataSet.hasOwnProperty('group')) {
    return (
      <>
        <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
          {dataSet.group}
        </ListSubheader>
      </>
    );
  }

  return (
    <>
      <Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
        { getOptionLabel(dataSet[1]) }
      </Typography>
    </>
  );
}

const OuterElementContext = createContext({});

const OuterElementType = forwardRef((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData = [];
  children.forEach((item) => {
    itemData.push(item);
    itemData.push(...(item.children || []));
  });

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });

  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child) => {
    if (child.hasOwnProperty('group')) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});


export default function VirtualizedAutocompleteSearcher({
  disabled,
  options, 
  control, 
  groupBy, 
  labels, 
  defaultValue, 
  id, 
  onChange
}) {
  labelsArr = labels;  
  
  const handleOnChange = (value) => { onChange(value); }

    const getVirtualizeOptionLabel = (option) => {
      const labelsArray = labels.map(lab => `${option[lab]}`);
      const label = labelsArray?.length > 1 ? labelsArray.join(" - ") : labelsArray[0];
      return label;
    }

    return (
      <Controller
          name={id}
          control={control}
          defaultValue={defaultValue}
          render={({ field: props }) => (
            <Autocomplete
                {...props}
                id={id}
                sx={{ width: "100%" }}
                onChange={(e, value)=> handleOnChange(value)}
                disableListWrap
                PopperComponent={StyledPopper}
                ListboxComponent={ListboxComponent}
                options={options}
                disabled={disabled ? true : false}
                groupBy={groupBy ? (option) => `${option[groupBy]}` : null}
                getOptionLabel={(option) =>  getVirtualizeOptionLabel(option)}
                renderOption={(props, option) => [props, option]}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => 
                  <TextField 
                    {...params} 
                    InputProps={{
                      ...params.InputProps,
                    }} />
                }
                renderGroup={(params) => params}
            />
          )}
        />
    );
}